import { useEffect, useState, useRef } from 'react';
import {
    Box, Typography, Button, Select, MenuItem, FormControl, InputLabel,
    IconButton, CircularProgress, Divider, Collapse, TextField, LinearProgress,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getSalesDocuments, uploadSalesDocument, deleteSalesDocument, generateSaleForms, generateSignLink } from '../../api/salesApi';

const PP = "'Poppins', sans-serif";
const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/api$/, '');

const CATEGORIES = [
    { value: 'Quotation', label: 'Signed Quotation' },
    { value: 'Signed',  label: 'Signed Document' },
    { value: 'Deposit', label: 'Deposit' },
    { value: 'Payment', label: 'Payment' },
    { value: 'Other',   label: 'Other' },
];

const SECTION_ORDER = ['Quotation', 'Signed', 'Deposit', 'Payment', 'Other'];
const SECTION_LABELS = {
    Quotation: 'Signed Quotation',
    Signed:  'Signed Documents',
    Deposit: 'Deposit Document',
    Payment: 'Payment Documents',
    Other:   'Other Documents',
};

function formatDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatBytes(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRM(n) {
    const v = Number(n) || 0;
    return `RM ${v.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function SalesDocumentsPanel({ salesId, canEdit, signedOn, depositOn, extraActions = null, afterActions = null, onDocsChanged, paidTotal = 0, totalPrice = 0, refreshKey = 0 }) {
    const [docs, setDocs] = useState([]);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [docsListOpen, setDocsListOpen] = useState(true);

    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [uploadCategory, setUploadCategory] = useState('Signed');
    const [uploadAmount, setUploadAmount] = useState('');
    const [uploadDate, setUploadDate] = useState('');   // payment date (Payment docs)
    const [uploadTitle, setUploadTitle] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);

    const [generatingForms, setGeneratingForms] = useState(false);
    const [generateError, setGenerateError] = useState('');

    const [signingLink, setSigningLink] = useState('');
    const [signingLinkError, setSigningLinkError] = useState('');
    const [generatingLink, setGeneratingLink] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    const linkInputRef = useRef(null);

    const fetchDocs = () => {
        if (!salesId) return;
        setLoadingDocs(true);
        getSalesDocuments(salesId)
            .then(r => setDocs(r.data || []))
            .catch(() => setDocs([]))
            .finally(() => setLoadingDocs(false));
    };

    // Re-fetch on salesId AND refreshKey — the parent bumps refreshKey after an edit
    // save (which regenerates the forms with new filenames), so the links stay current
    // instead of pointing at the now-deleted previous draft (404).
    useEffect(() => { fetchDocs(); }, [salesId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDelete = async (doc) => {
        if (!window.confirm(`Remove "${doc.fileName || doc.title}"?`)) return;
        try {
            await deleteSalesDocument(doc.salesDocumentId);
            fetchDocs();
            onDocsChanged?.();
        } catch {
            alert('Failed to delete document.');
        }
    };

    const isPaymentCat = uploadCategory === 'Deposit' || uploadCategory === 'Payment';

    const handleUpload = async () => {
        if (!selectedFile) { setUploadError('Please select a file.'); return; }
        // Deposit can only have ONE document — delete the existing one to replace it.
        if (uploadCategory === 'Deposit' && docs.some(d => d.category === 'Deposit')) {
            setUploadError('A deposit document already exists. Delete it first to upload another.');
            return;
        }
        if (isPaymentCat) {
            const amt = Number(uploadAmount);
            if (!uploadAmount || isNaN(amt) || amt <= 0) {
                setUploadError('Please enter the payment amount (RM).');
                return;
            }
        }
        setUploadError('');
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('salesId', salesId);
            fd.append('category', uploadCategory);
            fd.append('title', uploadTitle.trim() || selectedFile.name);
            fd.append('file', selectedFile);
            if (isPaymentCat) {
                fd.append('amount', String(Number(uploadAmount)));
                // Auto-label: Deposit category = "Deposit"; Payment category auto-numbers
                // "Payment N" from the highest existing Payment number + 1.
                let label = 'Deposit';
                if (uploadCategory === 'Payment') {
                    const nums = docs
                        .filter(d => d.category === 'Payment')
                        .map(d => { const m = /Payment\s*(\d+)/i.exec(d.paymentLabel || ''); return m ? Number(m[1]) : 0; });
                    const next = (nums.length ? Math.max(...nums) : 0) + 1;
                    label = `Payment ${next}`;
                }
                fd.append('paymentLabel', label);
            }
            // Payment/Deposit slip date. For a Deposit doc this also sets the sale's
            // deposit date (server-side, only when the sale has none yet).
            if ((uploadCategory === 'Payment' || uploadCategory === 'Deposit') && uploadDate) {
                fd.append('docDate', new Date(`${uploadDate}T12:00:00`).toISOString());
            }
            await uploadSalesDocument(fd);
            setSelectedFile(null);
            setUploadAmount('');
            setUploadDate('');
            setUploadTitle('');
            if (fileInputRef.current) fileInputRef.current.value = '';
            fetchDocs();
            onDocsChanged?.();
        } catch (err) {
            const data = err?.response?.data;
            const fieldErr = data?.errors && Object.values(data.errors).flat?.()[0];
            const tooLarge = (fieldErr || '').toLowerCase().includes('request body too large');
            const msg = tooLarge
                ? 'File is too large to upload (max 100 MB).'
                : data?.message || fieldErr || data?.title || 'Upload failed.';
            setUploadError(msg);
        } finally {
            setUploading(false);
        }
    };

    const handleGenerateForms = async () => {
        setGenerateError('');
        setGeneratingForms(true);
        try {
            await generateSaleForms(salesId, false);
            fetchDocs();
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data || 'Generate forms failed.';
            setGenerateError(typeof msg === 'string' ? msg : 'Generate forms failed.');
        } finally {
            setGeneratingForms(false);
        }
    };

    const handleGetSignLink = async () => {
        setSigningLinkError('');
        setSigningLink('');
        setLinkCopied(false);
        setGeneratingLink(true);
        try {
            // Re-signing ("Sign again"): regenerate a fresh, unsigned form set first
            // so the new link presents up-to-date documents with the previous
            // customer signature cleared, ready to be signed again.
            if (signedOn) {
                await generateSaleForms(salesId, false);
                fetchDocs();
                onDocsChanged?.();
            }
            const res = await generateSignLink(salesId);
            const fullUrl = `${window.location.origin}/sign/${res.data.token}`;
            setSigningLink(fullUrl);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data || 'Failed to generate signing link.';
            setSigningLinkError(typeof msg === 'string' ? msg : 'Failed to generate signing link.');
        } finally {
            setGeneratingLink(false);
        }
    };

    const handleCopyLink = () => {
        if (!signingLink) return;
        navigator.clipboard.writeText(signingLink).then(() => {
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2500);
        }).catch(() => {
            // Fallback: select text
            if (linkInputRef.current) {
                linkInputRef.current.select();
                document.execCommand('copy');
                setLinkCopied(true);
                setTimeout(() => setLinkCopied(false), 2500);
            }
        });
    };

    // Group docs by category; only show Other section if Other docs exist
    const grouped = {};
    docs.forEach(d => {
        const cat = d.category || 'Other';
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(d);
    });

    const visibleSections = SECTION_ORDER.filter(cat =>
        cat !== 'Other' ? true : (grouped['Other'] && grouped['Other'].length > 0)
    );

    const pct = totalPrice > 0 ? Math.min(100, (paidTotal / totalPrice) * 100) : 0;
    const fullyPaid = totalPrice > 0 && paidTotal >= totalPrice;
    const barColor = fullyPaid ? '#22C55E' : '#1A8CFF';
    const progressBar = (
        <Box sx={{ mb: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.4 }}>
                <Typography sx={{ fontFamily: PP, fontSize: '0.74rem', color: '#334155', fontWeight: 600 }}>
                    {formatRM(paidTotal)} / {formatRM(totalPrice)}
                </Typography>
                {fullyPaid ? (
                    <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        background: '#F0FDF4', border: '1px solid #BBF7D0',
                        borderRadius: '6px', px: 0.75, py: 0.15
                    }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: '#22C55E' }} />
                        <Typography sx={{ fontFamily: PP, fontSize: '0.68rem', color: '#15803D', fontWeight: 700 }}>
                            Fully Paid
                        </Typography>
                    </Box>
                ) : (
                    <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#64748B', fontWeight: 600 }}>
                        {pct.toFixed(0)}%
                    </Typography>
                )}
            </Box>
            <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                    height: 8, borderRadius: 4, background: '#E2E8F0',
                    '& .MuiLinearProgress-bar': { background: barColor, borderRadius: 4 }
                }}
            />
        </Box>
    );

    const docsList = loadingDocs ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
        </Box>
    ) : (
        visibleSections.map((cat, idx) => (
            <Box key={cat} sx={{ mb: 2 }}>
                {idx > 0 && <Divider sx={{ mb: 1.5 }} />}
                <Typography sx={{
                    fontFamily: PP, fontWeight: 600, fontSize: '0.75rem',
                    color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75
                }}>
                    {SECTION_LABELS[cat]}
                </Typography>

                {cat === 'Payment' && progressBar}

                {(!grouped[cat] || grouped[cat].length === 0) ? (
                    <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#94A3B8', fontStyle: 'italic', mb: 0.5 }}>
                        No documents
                    </Typography>
                ) : (
                    grouped[cat].map(doc => {
                        const absoluteUrl = doc.fileUrl
                            ? `${API_BASE}${doc.fileUrl}`
                            : null;
                        return (
                            <Box key={doc.salesDocumentId} sx={{
                                display: 'flex', alignItems: 'center', gap: 0.5,
                                py: 0.6, px: 0.5, borderRadius: '6px',
                                '&:hover': { background: '#F8FAFC' }
                            }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {absoluteUrl ? (
                                        <Typography
                                            component="a"
                                            href={absoluteUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                fontFamily: PP, fontSize: '0.8rem', color: '#1A8CFF',
                                                textDecoration: 'none', display: 'block',
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                '&:hover': { textDecoration: 'underline' }
                                            }}
                                        >
                                            {doc.title || doc.fileName}
                                        </Typography>
                                    ) : (
                                        <Typography sx={{
                                            fontFamily: PP, fontSize: '0.8rem', color: '#0F172A',
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                        }}>
                                            {doc.title || doc.fileName}
                                        </Typography>
                                    )}
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#94A3B8' }}>
                                        {formatDate(doc.uploadedOn)}{doc.fileSize ? ` · ${formatBytes(doc.fileSize)}` : ''}
                                    </Typography>
                                    {(doc.amount != null || doc.paymentLabel) && (
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#0F766E', fontWeight: 600 }}>
                                            {doc.paymentLabel ? doc.paymentLabel : 'Payment'}
                                            {doc.amount != null ? ` · ${formatRM(doc.amount)}` : ''}
                                            {doc.docDate ? ` · ${formatDate(doc.docDate)}` : ''}
                                        </Typography>
                                    )}
                                </Box>
                                {canEdit && (
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDelete(doc)}
                                        sx={{ color: '#CBD5E1', '&:hover': { color: '#EF4444' }, flexShrink: 0 }}
                                        title="Remove document"
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        );
                    })
                )}
            </Box>
        ))
    );

    return (
        <Box sx={{ fontFamily: PP }}>
            {canEdit && (
                <>
                    {/* ── Actions (always visible): Signature · ClickUp · Regenerate ── */}
                    <Box sx={{ mb: 1.5 }}>
                        <Typography sx={{
                            fontFamily: PP, fontWeight: 600, fontSize: '0.75rem',
                            color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75
                        }}>
                            Customer Signature
                        </Typography>

                        {signedOn ? (
                            <Box sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.75,
                                background: '#F0FDF4', border: '1px solid #BBF7D0',
                                borderRadius: '8px', px: 1.25, py: 0.5, mb: 0.75
                            }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#22C55E', flexShrink: 0 }} />
                                <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#15803D', fontWeight: 500 }}>
                                    Signed on {new Date(signedOn).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </Typography>
                            </Box>
                        ) : null}

                        <Box>
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={generatingLink}
                                onClick={handleGetSignLink}
                                sx={{
                                    fontFamily: PP, textTransform: 'none', fontSize: '0.78rem',
                                    borderColor: '#CBD5E1', color: '#475569', borderRadius: '8px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {generatingLink
                                    ? <><CircularProgress size={13} sx={{ mr: 0.75, color: '#94A3B8' }} />Generating...</>
                                    : (signedOn ? 'Re-sign (new link)' : 'Get signature link')}
                            </Button>
                        </Box>

                        {signingLinkError && (
                            <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#EF4444', mt: 0.5 }}>
                                {signingLinkError}
                            </Typography>
                        )}

                        {signingLink && (
                            <Box sx={{ mt: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                    <input
                                        ref={linkInputRef}
                                        readOnly
                                        value={signingLink}
                                        style={{
                                            flex: 1, fontFamily: PP, fontSize: '0.72rem',
                                            border: '1px solid #E2E8F0', borderRadius: '6px',
                                            padding: '4px 8px', color: '#334155',
                                            background: '#F8FAFC', minWidth: 0
                                        }}
                                    />
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleCopyLink}
                                        sx={{
                                            fontFamily: PP, textTransform: 'none', fontSize: '0.72rem',
                                            borderColor: linkCopied ? '#22C55E' : '#CBD5E1',
                                            color: linkCopied ? '#15803D' : '#475569',
                                            borderRadius: '6px', whiteSpace: 'nowrap', flexShrink: 0, minWidth: 56
                                        }}
                                    >
                                        {linkCopied ? 'Copied' : 'Copy'}
                                    </Button>
                                </Box>
                                <Button
                                    component="a"
                                    href={signingLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="small"
                                    variant="outlined"
                                    startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                                    sx={{
                                        mt: 0.25, fontFamily: PP, textTransform: 'none', fontSize: '0.72rem',
                                        borderColor: '#CBD5E1', color: '#1A8CFF', borderRadius: '6px', py: 0.25,
                                    }}
                                >
                                    Open link
                                </Button>
                            </Box>
                        )}
                    </Box>

                    {extraActions && (
                        <>
                            <Divider sx={{ my: 1.5 }} />
                            {extraActions}
                        </>
                    )}

                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ mb: 1.5 }}>
                        <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '0.75rem', color: '#0F172A', mb: 0.25 }}>
                            Regenerate Forms
                        </Typography>
                        <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#94A3B8', mb: 0.75 }}>
                            Build the quotation &amp; forms from this sale's stored data, then download each PDF from the Documents list. Re-generate after editing the sale to refresh them.
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={generatingForms}
                            onClick={handleGenerateForms}
                            sx={{
                                fontFamily: PP, textTransform: 'none', fontSize: '0.78rem',
                                borderColor: '#CBD5E1', color: '#475569', borderRadius: '8px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {generatingForms
                                ? <><CircularProgress size={13} sx={{ mr: 0.75, color: '#94A3B8' }} />Generating...</>
                                : 'Generate forms'}
                        </Button>
                        {generateError && (
                            <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#EF4444', mt: 0.5 }}>
                                {generateError}
                            </Typography>
                        )}
                    </Box>

                    {afterActions && (
                        <>
                            <Divider sx={{ my: 1.5 }} />
                            {afterActions}
                        </>
                    )}
                </>
            )}

            {/* ── Documents (collapsible): all docs list + upload ── */}
            <Divider sx={{ my: 1.5 }} />
            <Box
                onClick={() => setDocsListOpen(o => !o)}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            >
                <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '0.9rem', color: '#0F172A' }}>
                    Documents
                </Typography>
                <IconButton size="small" sx={{ color: '#64748B' }}>
                    {docsListOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Collapse in={docsListOpen} unmountOnExit>
                <Box sx={{ pt: 1 }}>
                    {docsList}

                    {canEdit && (
                        <>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography sx={{ fontFamily: PP, fontWeight: 600, fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                                Upload Document
                            </Typography>

                            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                                <InputLabel sx={{ fontFamily: PP, fontSize: '0.82rem' }}>Category</InputLabel>
                                <Select
                                    value={uploadCategory}
                                    label="Category"
                                    onChange={e => setUploadCategory(e.target.value)}
                                    sx={{ fontFamily: PP, fontSize: '0.82rem', borderRadius: '8px' }}
                                >
                                    {CATEGORIES.map(c => (
                                        <MenuItem key={c.value} value={c.value} sx={{ fontFamily: PP, fontSize: '0.82rem' }}>
                                            {c.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                size="small"
                                label="Title / description (optional)"
                                placeholder="e.g. TNB bill, ATAP approval, signed SPA"
                                value={uploadTitle}
                                onChange={e => setUploadTitle(e.target.value)}
                                helperText="Shown as the document name. Leave blank to use the file name."
                                sx={{ mb: 1, '& .MuiInputBase-input': { fontFamily: PP, fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontFamily: PP, fontSize: '0.82rem' }, '& .MuiFormHelperText-root': { fontFamily: PP, fontSize: '0.68rem', mx: 0 } }}
                            />

                            {isPaymentCat && (
                                <>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label="Amount (RM)"
                                        value={uploadAmount}
                                        onChange={e => { setUploadAmount(e.target.value); setUploadError(''); }}
                                        inputProps={{ min: 0, step: '0.01' }}
                                        sx={{ mb: 1, '& .MuiInputBase-input': { fontFamily: PP, fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontFamily: PP, fontSize: '0.82rem' } }}
                                    />
                                    {(uploadCategory === 'Payment' || (uploadCategory === 'Deposit' && !depositOn)) && (
                                        <>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="date"
                                                label={uploadCategory === 'Deposit' ? 'Deposit Date' : 'Payment Date (optional)'}
                                                value={uploadDate}
                                                onChange={e => setUploadDate(e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                                sx={{ mb: 1, '& .MuiInputBase-input': { fontFamily: PP, fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontFamily: PP, fontSize: '0.82rem' } }}
                                            />
                                            {uploadCategory === 'Deposit' && (
                                                <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#94A3B8', mb: 1 }}>
                                                    Sets the sale&rsquo;s deposit date (it has none yet).
                                                </Typography>
                                            )}
                                        </>
                                    )}
                                    {uploadCategory === 'Payment' && (
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#94A3B8', mb: 1 }}>
                                            Auto-labelled Payment 1, Payment 2, … in upload order.
                                        </Typography>
                                    )}
                                </>
                            )}

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<UploadFileIcon />}
                                    onClick={() => fileInputRef.current?.click()}
                                    sx={{
                                        fontFamily: PP, textTransform: 'none', fontSize: '0.78rem',
                                        borderColor: '#CBD5E1', color: '#475569', borderRadius: '8px',
                                        whiteSpace: 'nowrap', flexShrink: 0
                                    }}
                                >
                                    Choose File
                                </Button>
                                <Typography sx={{
                                    fontFamily: PP, fontSize: '0.75rem', color: '#64748B',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1
                                }}>
                                    {selectedFile ? selectedFile.name : 'No file chosen'}
                                </Typography>
                            </Box>

                            <input
                                ref={fileInputRef}
                                type="file"
                                style={{ display: 'none' }}
                                onChange={e => {
                                    setSelectedFile(e.target.files[0] || null);
                                    setUploadError('');
                                }}
                            />

                            {uploadError && (
                                <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#EF4444', mb: 0.75 }}>
                                    {uploadError}
                                </Typography>
                            )}

                            <Button
                                fullWidth
                                size="small"
                                variant="contained"
                                disabled={uploading || !selectedFile}
                                onClick={handleUpload}
                                sx={{
                                    fontFamily: PP, textTransform: 'none', fontSize: '0.8rem',
                                    fontWeight: 600, borderRadius: '8px', boxShadow: 'none',
                                    background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
                                }}
                            >
                                {uploading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Upload'}
                            </Button>
                        </>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
}
